import type { UseFormReturn } from 'react-hook-form';
import { ExternalLink, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Sip } from '@sbclite/core';
import type { SipPeerFormData } from '../schema';

export function ProfileTab({
  form,
  profiles,
  profileLocked = false,
}: {
  readonly form: UseFormReturn<SipPeerFormData>;
  readonly profiles: readonly Sip.SipProfileInfo[];
  readonly profileLocked?: boolean;
}) {
  const profileId = form.watch('sipProfileId');
  const selectedProfile = profiles.find((profile) => profile.id === profileId) ?? null;
  const enabledProfiles = profiles.filter((profile) => profile.enabled);
  const selectableProfiles = selectedProfile && !selectedProfile.enabled
    ? [selectedProfile, ...enabledProfiles.filter((profile) => profile.id !== selectedProfile.id)]
    : enabledProfiles;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Profile attachment</h3>
        <p className="text-sm text-gray-600">Attach an enabled SIP profile to centralize session timer, registrar, digest, and media policy.</p>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <h4 className="font-medium text-gray-900 flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-gray-600" />
          SIP profile
        </h4>
        <p className="text-xs text-gray-500">Only enabled profiles can be attached to new peers. Edit profiles on the SIP Profiles page.</p>
        <select
          {...form.register('sipProfileId')}
          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          disabled={profileLocked}
        >
          <option value="">No profile attached</option>
          {selectableProfiles.map((profile) => (
            <option key={profile.id} value={profile.id} disabled={!profile.enabled}>
              {profile.name}{!profile.enabled ? ' (disabled)' : ''}
            </option>
          ))}
        </select>

        {selectedProfile ? (
          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 space-y-2">
            <div>
              Attached profile: <span className="font-medium">{selectedProfile.name}</span>
            </div>
            <Link to="/connectivity/sip-profiles" className="inline-flex items-center gap-1 text-blue-700 hover:underline">
              Open SIP Profiles
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
        ) : (
          <p className="text-sm text-gray-600">No profile attached. Legacy peer fields remain editable.</p>
        )}
      </div>
    </div>
  );
}
